import { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import Modal, { ModalHeader, ModalBody, ModalFooter } from '../components/Modal';
import { userService } from '../api/services';

const AVATAR_COLORS = ['#C8401E', '#1C5FAD', '#1A7A4A', '#B8720A', '#5B3E9E', '#0E7490', '#92400E', '#BE185D'];

export default function InviteUserModal() {
  const { modals, closeModal, roles, users, setUsers, showToast } = useApp();
  const open = !!modals['invite-user'];
  const editId = modals['invite-user-id'];
  const editUser = editId ? users.find(u => u.id === editId) : null;
  const isEdit = !!editUser;

  const [fname, setFname] = useState('');
  const [lname, setLname] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [roleId, setRoleId] = useState('');
  const [color, setColor] = useState(AVATAR_COLORS[0]);
  const [invited, setInvited] = useState(false);

  useEffect(() => {
    if (open) {
      if (editUser) {
        setFname(editUser.fname || '');
        setLname(editUser.lname || '');
        setEmail(editUser.email || '');
        setPhone(editUser.phone || '');
        setPassword('');
        setRoleId(editUser.roleId || roles[1]?.id || '');
        setColor(editUser.color || AVATAR_COLORS[0]);
      } else {
        setFname(''); setLname(''); setEmail(''); setPhone(''); setPassword('');
        setRoleId(roles[1]?.id || '');
        setColor(AVATAR_COLORS[Math.floor(Math.random() * AVATAR_COLORS.length)]);
      }
      setInvited(false);
      setShowPw(false);
    }
  }, [open, roles]);

  function close() { closeModal('invite-user'); }

  function submit() {
    if (!email.trim()) { alert('Email is required'); return; }
    if (!email.includes('@')) { alert('Enter a valid email address'); return; }
    if (!fname.trim()) { alert('First name is required'); return; }
    if (!isEdit && !password.trim()) { alert('Password is required'); return; }
    if (!isEdit && password.length < 6) { alert('Password must be at least 6 characters'); return; }
    if (!isEdit && users.find(u => u.email === email)) { alert('A user with this email already exists'); return; }

    const initials = (fname[0] || '') + (lname[0] || '');

    if (isEdit) {
      const updates = { fname, lname, email, phone, roleId, color, initials: initials.toUpperCase() };
      if (password.trim()) updates.password = password;
      setUsers(prev => prev.map(u => u.id === editUser.id ? { ...u, ...updates } : u));
      const backendId = editUser._backendId || editUser.id;
      userService.update(backendId, { first_name: fname, last_name: lname, email, role_id: parseInt(roleId), avatar_color: color, password: password.trim() || undefined }).catch(() => {});
      showToast(`${fname} ${lname} updated`);
      close();
      return;
    }

    const id = 'u' + Date.now().toString(36);
    const newUser = {
      id, fname, lname, email, phone, roleId, password,
      status: 'active', lastActive: 'Not yet',
      initials: initials.toUpperCase(), color,
    };
    // Persist to API
    userService.create({ first_name: fname, last_name: lname, email, password, role_id: parseInt(roleId), avatar_color: color }).then(created => {
      setUsers(prev => prev.map(u => u.id === id ? created : u));
    }).catch(() => {});
    setUsers(prev => [...prev, newUser]);
    setInvited(true);
    showToast(`✅ ${fname} ${lname} account created`);
  }

  const selectedRole = roles.find(r => r.id === roleId);

  return (
    <Modal open={open} onClose={close} size="sm">
      <ModalHeader
        icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 16, height: 16 }}><path d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="8.5" cy="7" r="4"/><line x1="20" y1="8" x2="20" y2="14"/><line x1="23" y1="11" x2="17" y2="11"/></svg>}
        title={isEdit ? 'Edit Team Member' : 'Add Team Member'}
        subtitle={isEdit ? `Editing ${editUser?.fname} ${editUser?.lname}` : 'Create a new user account'}
        onClose={close}
      />

      {invited ? (
        <ModalBody>
          <div style={{ textAlign: 'center', padding: '20px 10px' }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>✅</div>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>Account Created!</div>
            <div style={{ fontSize: 12, color: 'var(--tx3)', marginBottom: 16 }}>
              <strong>{fname} {lname}</strong> can now log in with:<br />
              <span style={{ fontFamily: 'monospace', background: 'var(--sur2)', padding: '2px 6px', borderRadius: 4, display: 'inline-block', marginTop: 4 }}>{email}</span>
            </div>
            <button className="btn btn-primary" onClick={close}>Done</button>
          </div>
        </ModalBody>
      ) : (
        <>
          <ModalBody>
            {/* Email */}
            <div className="fg">
              <label className="fl">Email Address *</label>
              <input className="fi" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="user@fleetrent.cy" />
            </div>

            {/* Name */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div className="fg">
                <label className="fl">First Name *</label>
                <input className="fi" value={fname} onChange={e => setFname(e.target.value)} placeholder="Andreas" />
              </div>
              <div className="fg">
                <label className="fl">Last Name</label>
                <input className="fi" value={lname} onChange={e => setLname(e.target.value)} placeholder="Papadopoulos" />
              </div>
            </div>

            {/* Role */}
            <div className="fg">
              <label className="fl">Role *</label>
              <select className="fi" value={roleId} onChange={e => setRoleId(e.target.value)}>
                {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
              {selectedRole && (
                <div style={{ fontSize: 10, color: 'var(--tx3)', marginTop: 3 }}>{selectedRole.desc}</div>
              )}
            </div>

            {/* Password */}
            <div className="fg">
              <label className="fl">{isEdit ? 'New Password (leave blank to keep current)' : 'Password *'}</label>
              <div style={{ position: 'relative' }}>
                <input
                  className="fi"
                  type={showPw ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder={isEdit ? 'Leave blank to keep current' : 'Min 6 characters'}
                  style={{ paddingRight: 36 }}
                />
                <button
                  type="button"
                  onClick={() => setShowPw(p => !p)}
                  style={{
                    position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                    border: 'none', background: 'transparent', cursor: 'pointer',
                    fontSize: 14, color: 'var(--tx3)', padding: 2,
                  }}
                  title={showPw ? 'Hide password' : 'Show password'}
                >
                  {showPw ? '🙈' : '👁️'}
                </button>
              </div>
            </div>

            {/* Phone */}
            <div className="fg">
              <label className="fl">Phone (optional)</label>
              <input className="fi" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+357 99 000 000" />
            </div>

            {/* Avatar Color */}
            <div className="fg">
              <label className="fl">Avatar Color</label>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
                {AVATAR_COLORS.map(c => (
                  <div
                    key={c}
                    onClick={() => setColor(c)}
                    style={{
                      width: 26, height: 26, borderRadius: '50%', background: c,
                      cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      border: color === c ? '3px solid var(--tx1)' : '2px solid transparent',
                      boxSizing: 'border-box', fontSize: 10, color: '#fff', fontWeight: 700,
                    }}
                  >
                    {(fname[0] || '') + (lname[0] || '')}
                  </div>
                ))}
              </div>
            </div>
          </ModalBody>

          <ModalFooter>
            <button className="btn btn-ghost" onClick={close}>Cancel</button>
            <button className="btn btn-primary" onClick={submit}>{isEdit ? 'Save Changes' : 'Create Account'}</button>
          </ModalFooter>
        </>
      )}
    </Modal>
  );
}
